import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import { z } from "zod";

import {
  Button,
  CancelSubmitButtons,
  CardInputsContainer,
} from "~/common/components";
import { PRONOUNS, RELATIONSHIPS } from "~/patients/constants";
import { useFormatHook } from "~/patients/hooks";
import { useUpdateUserDetails } from "~/patients/hooks/useUpdateUserDetails";
import {
  AccountSchemaType,
  PatientInformation,
  PronounsOptions,
  updatePatientUserDetailsSchema,
} from "~/patients/utils";
import { AddressFieldset } from "./AddressFieldset";
import { EmergencyContactFieldset } from "./EmergencyContactFieldset";
import { PersonalDetailsFieldset } from "./PersonalDetailsFieldset";

export type PatientSchemaType = z.infer<typeof updatePatientUserDetailsSchema>;

interface UserDetailsProps {
  currentPatient: PatientInformation;
}

export const UserDetails = ({ currentPatient }: UserDetailsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { transformedPatientData } = useFormatHook();

  const { mutate: updateUserDetails } = useUpdateUserDetails();

  const updatePatientForm = useForm<AccountSchemaType>({
    defaultValues: transformedPatientData(currentPatient),
    resolver: zodResolver(updatePatientUserDetailsSchema),
  });

  const onEdit = () => setIsEditing(true);

  const onCancel = () => {
    updatePatientForm.reset();
    setIsEditing(false);
  };

  const onSubmit: SubmitHandler<AccountSchemaType> = (data) => {
    let selectedPronouns = data.pronouns?.pronouns;
    if (
      data.pronouns?.pronouns === PRONOUNS.other &&
      data.pronouns?.customPronouns
    ) {
      selectedPronouns = data.pronouns.customPronouns;
    }
    const parts = selectedPronouns?.split("/");
    const pronouns: PronounsOptions | null = parts
      ? {
          subject: parts[0],
          object: parts[1],
          possessive: parts[2],
        }
      : null;

    const contact = {
      ...data.emergency_contacts[0],
      relationship:
        data.emergency_contacts[0].relationship === RELATIONSHIPS.OTHER &&
        data.emergency_contacts[0].customRelationship
          ? data.emergency_contacts[0].customRelationship
          : data.emergency_contacts[0].relationship,
    };

    const userData = {
      ...data,
      pronouns,
      emergency_contacts: [{ ...contact }],
    };
    updateUserDetails(
      { userData, userId: currentPatient.id },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      },
    );
  };

  return (
    <CardInputsContainer
      title="User details"
      containerClassName="flex-row items-center"
      actions={
        isEditing ? (
          <CancelSubmitButtons
            containerClassName="w-auto"
            submitButtonLabel="Save"
            handleCancel={onCancel}
            handleSubmit={updatePatientForm.handleSubmit(onSubmit)}
            submitButtonType="button"
          />
        ) : (
          <Button onClick={onEdit} className="bg-white" variant="outline">
            Edit
          </Button>
        )
      }
    >
      <FormProvider {...updatePatientForm}>
        <form className="flex w-full flex-col gap-4 md:max-w-[49rem]">
          <PersonalDetailsFieldset
            currentPatient={currentPatient}
            disabled={!isEditing}
          />
          <AddressFieldset disabled={!isEditing} />
          <EmergencyContactFieldset disabled={!isEditing} />
        </form>
      </FormProvider>
    </CardInputsContainer>
  );
};
